import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ICONS, type IconName } from './icons';
import { ApiError } from '../api/client';
import { daysBetween, today } from '../lib/format';

/** Stroke icon. The markup is trusted static SVG from icons.ts. */
export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: ICONS[name] }} />
  );
}

// ---- Toasts ----
const ToastContext = createContext<(text: string) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState('');
  const [shown, setShown] = useState(false);
  const timer = useRef<number>();
  const toast = useCallback((t: string) => {
    setText(t);
    setShown(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShown(false), 3200);
  }, []);
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className={`toast ${shown ? 'show' : ''}`} role="status" aria-live="polite">
        <Icon name="check" size={15} />
        <span>{text}</span>
      </div>
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);

// ---- Modal dialogs ----
/**
 * A modal dialog. Escape and the scrim close it, and focus returns to where it was. Its submit stops
 * here: React passes portal events up the component tree, so a dialog opened from a form (a new
 * vendor from the bill editor) must never submit that form too.
 */
export function Modal({ title, onClose, children, footer, wide, onSubmit }: { title: string; onClose: () => void; children: ReactNode; footer: ReactNode; wide?: boolean; onSubmit?: () => void }) {
  const ref = useRef<HTMLFormElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[autofocus], input:not([readonly]), textarea, select, button.primary');
    first?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (returnTo && document.contains(returnTo)) returnTo.focus(); };
  }, []);
  const id = `dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;
  return createPortal(
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form ref={ref} className={`dialog ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={id} noValidate onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit?.(); }}>
        <div className="d-head"><h2 id={id}>{title}</h2><span className="spacer" /><button type="button" className="btn ghost small" onClick={onClose} aria-label="Close"><Icon name="x" /></button></div>
        <div className="d-body">{children}</div>
        <div className="d-foot">{footer}</div>
      </form>
    </div>,
    document.body,
  );
}

// ---- Popover menus ----
/** A menu anchored to a button: beside it when the button is on the rail, below it otherwise. */
export function Popover({ anchor, onClose, children, label }: { anchor: HTMLElement; onClose: () => void; children: ReactNode; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = anchor.getBoundingClientRect();
    const beside = !!anchor.closest('.rail, .side-mini') && window.innerWidth > 860;
    const left = beside ? r.right + 10 : Math.min(r.left, window.innerWidth - el.offsetWidth - 12);
    const top = beside ? Math.min(r.top, window.innerHeight - el.offsetHeight - 12) : r.bottom + 8;
    setPos({ left: Math.max(12, left), top: Math.max(12, top) });
    anchor.setAttribute('aria-expanded', 'true');
    el.querySelector<HTMLElement>('button.pop-item, a.pop-item')?.focus();
    return () => anchor.setAttribute('aria-expanded', 'false');
  }, [anchor]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { onClose(); anchor.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [anchor, onClose]);
  return createPortal(
    <div ref={ref} className="pop" role="menu" aria-label={label} style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden' }} onClick={(e) => { if ((e.target as HTMLElement).closest('.pop-item:not(.static)')) onClose(); }}>
      {children}
    </div>,
    document.body,
  );
}
export function PopItem({ icon, title, sub, onClick, to, extra, checked }: { icon?: IconName; title: ReactNode; sub?: ReactNode; onClick?: () => void; to?: string; extra?: ReactNode; checked?: boolean }) {
  const inner = (
    <>
      {icon && <span className="pop-ic"><Icon name={icon} /></span>}
      <span style={{ minWidth: 0, flex: 1 }}><span className="pi-title" style={{ display: 'block' }}>{title}</span>{sub && <span className="pi-sub" style={{ display: 'block' }}>{sub}</span>}</span>
      {extra}
    </>
  );
  const role = checked === undefined ? 'menuitem' : 'menuitemradio';
  if (to) return <Link className="pop-item" role={role} aria-checked={checked} to={to}>{inner}</Link>;
  return <button type="button" className="pop-item" role={role} aria-checked={checked} onClick={onClick}>{inner}</button>;
}

/** State for a popover opened from a button: open(anchor) / close(). */
export function usePopover() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = useCallback(() => setAnchor(null), []);
  const toggle = useCallback((e: { currentTarget: HTMLElement }) => { const t = e.currentTarget; setAnchor((a) => (a === t ? null : t)); }, []);
  return { anchor, toggle, close };
}

// ---- Page furniture ----
export function PageHead({ title, sub, actions, crumb }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; crumb?: { to: string; label: string } }) {
  return (
    <>
      {crumb && <Link className="crumb" to={crumb.to}><Icon name="back" size={15} />{crumb.label}</Link>}
      <div className="page-head">
        <div><h1>{title}</h1>{sub && <div className="sub">{sub}</div>}</div>
        {actions && <div className="actions">{actions}</div>}
      </div>
    </>
  );
}
export function Loading({ label = 'Loading' }: { label?: string }) {
  return <div className="card empty" aria-busy="true">{label}…</div>;
}
export function ErrorNotice({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="card empty" role="alert">
      <b>Could not load this.</b> {message} {retry && <button className="link-btn" onClick={retry}>Try again</button>}
    </div>
  );
}
/** The message to show for a failed action. */
export const errorText = (error: unknown) => (error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Something went wrong');

// ---- Document status ----
export interface StatusLike { status?: string; is_overdue?: boolean; due_date?: string; amount_paid?: string; kind?: string }
/** The pill for an invoice or bill, matching the prototype: overdue shows how many days. */
export function statusOf(d: StatusLike): { key: string; label: string } {
  if (d.status === 'void') return { key: 'void', label: 'Void' };
  if (d.status === 'draft') return { key: 'draft', label: 'Draft' };
  if (d.status === 'paid') return { key: 'paid', label: 'Paid' };
  if (d.is_overdue && d.due_date) {
    const late = daysBetween(d.due_date, today());
    return { key: 'overdue', label: `Overdue · ${late} ${late === 1 ? 'day' : 'days'}` };
  }
  if (d.status === 'partial') return { key: 'partial', label: 'Partially paid' };
  return { key: 'open', label: d.kind === 'bill' ? 'Unpaid' : 'Open' };
}
export function StatusPill({ doc }: { doc: StatusLike }) {
  const s = statusOf(doc);
  return <span className={`pill status ${s.key}`}>{s.label}</span>;
}
